import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import PagesIcon from "@mui/icons-material/Pages";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from "@mui/material";
import I18n from "i18n";
import { useState } from "react";

export interface DataGridPaginationProps {
  page: number; // 0-based
  rowsPerPage: number;
  count: number;
  onPageChange: (event: any, newPage: number) => void;
  onRowsPerPageChange: (event: any) => void;
  rowsPerPageOptions?: number[];
}

export default function DataGridPagination({
  page,
  rowsPerPage,
  count,
  onPageChange,
  onRowsPerPageChange,
  rowsPerPageOptions = [10, 25, 50, 100, 500],
}: DataGridPaginationProps) {
  const [jumpOpen, setJumpOpen] = useState(false);

  const totalPages = Math.max(1, Math.ceil(count / rowsPerPage));

  const changePage = (e: any, newPage: number) => {
    onPageChange(e, newPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: { xs: "flex-start", sm: "flex-end" },
          gap: 1,
          px: 2,
          py: 0.5,
          flexWrap: "wrap",
          minHeight: 52,
        }}
      >
        {/* Rows per page */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mr: 1 }}>
          {/*<Typography variant="body2" color="text.secondary" noWrap>
            {I18n.t("DataGrid.rowsPerPage")}:
          </Typography>*/}
          <FormControl size="medium" variant="standard">
            <Select
              value={rowsPerPage}
              onChange={e =>
                onRowsPerPageChange({ target: { value: e.target.value } })
              }
              disableUnderline
              sx={{ fontSize: 12 }}
            >
              {rowsPerPageOptions.map(opt => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Total count */}
        <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
          {I18n.t("DataGrid.total", { count })}
        </Typography>

        {/* Navigation */}
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Tooltip title={I18n.t("DataGrid.firstPage")}>
            <span>
              <IconButton
                size="medium"
                onClick={e => changePage(e, 0)}
                disabled={page === 0}
              >
                <FirstPageIcon fontSize="medium" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={I18n.t("DataGrid.prevPage")}>
            <span>
              <IconButton
                size="medium"
                onClick={e => changePage(e, page - 1)}
                disabled={page === 0}
              >
                <NavigateBeforeIcon fontSize="medium" />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title={I18n.t("DataGrid.jumpToPage")}>
            <Button
              size="medium"
              variant="text"
              onClick={() => setJumpOpen(true)}
              sx={{ minWidth: 60, fontVariantNumeric: "tabular-nums" }}
            >
              {I18n.t("DataGrid.pageOf", { page: page + 1, total: totalPages })}
            </Button>
          </Tooltip>

          <Tooltip title={I18n.t("DataGrid.nextPage")}>
            <span>
              <IconButton
                size="medium"
                onClick={e => changePage(e, page + 1)}
                disabled={page >= totalPages - 1}
              >
                <NavigateNextIcon fontSize="medium" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={I18n.t("DataGrid.lastPage")}>
            <span>
              <IconButton
                size="medium"
                onClick={e => changePage(e, totalPages - 1)}
                disabled={page >= totalPages - 1}
              >
                <LastPageIcon fontSize="medium" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {/* Jump-to-page modal */}
      <Dialog
        open={jumpOpen}
        onClose={() => setJumpOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <PagesIcon fontSize="medium" />
          {I18n.t("DataGrid.jumpToPage")}
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
              pt: 1,
              overflowY: "auto",
            }}
          >
            {Array.from({ length: totalPages }, (_, i) => (
              <Button
                key={i}
                variant={i === page ? "contained" : "outlined"}
                size="medium"
                sx={{ minWidth: 48 }}
                onClick={() => {
                  changePage(null, i);
                  setJumpOpen(false);
                }}
              >
                {i + 1}
              </Button>
            ))}
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}
